import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GripVertical, Camera, Loader2, Trash2, Eye, EyeOff, Plus, Sparkles, Save, X, ExternalLink, ChevronDown, Pencil, Crosshair, LayoutGrid } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import LayoutBuilder from "../components/admin/LayoutBuilder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLooksManager() {
  const navigate = useNavigate();
  const token = localStorage.getItem("adminToken");
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [dragIdx, setDragIdx] = useState(null);
  const [products, setProducts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editItemsLook, setEditItemsLook] = useState(null);
  const [layoutLook, setLayoutLook] = useState(null);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const menuRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchLooks = useCallback(async () => {
    try {
      const r = await fetch(`${API}/ai/admin/looks`, { headers });
      const d = await r.json();
      setLooks(d.looks || []);
    } catch { toast.error("Failed to load looks"); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    fetchLooks();
    fetch(`${API}/outfits?limit=200`, { headers }).then(r => r.json()).then(d => setProducts(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const updateLook = async (lookId, updates) => {
    setSaving(p => ({ ...p, [lookId]: true }));
    try {
      await fetch(`${API}/ai/admin/looks/${lookId}`, { method: "PUT", headers, body: JSON.stringify(updates) });
      setLooks(prev => prev.map(l => l.id === lookId ? { ...l, ...updates } : l));
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    setSaving(p => ({ ...p, [lookId]: false }));
  };

  const deleteLook = async (lookId, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await fetch(`${API}/ai/admin/looks/${lookId}`, { method: "DELETE", headers });
      setLooks(prev => prev.filter(l => l.id !== lookId));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const uploadCover = async (lookId, file) => {
    setSaving(p => ({ ...p, [lookId]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/upload/image`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const { url } = await r.json();
      await updateLook(lookId, { cover_image: url });
    } catch { toast.error("Upload failed"); }
    setSaving(p => ({ ...p, [lookId]: false }));
  };

  // Drag and drop
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setLooks(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
  };
  const handleDragEnd = async () => {
    setDragIdx(null);
    try {
      await fetch(`${API}/ai/admin/looks/reorder`, { method: "PUT", headers, body: JSON.stringify({ ordered_ids: looks.map(l => l.id) }) });
      toast.success("Order saved");
    } catch { toast.error("Reorder failed"); }
  };

  const handleAiGenerate = async () => {
    setShowNewMenu(false);
    setAiGenerating(true);
    try {
      const r = await fetch(`${API}/ai/admin/generate-looks-batch`, { method: "POST", headers });
      if (!r.ok) throw new Error((await r.json()).detail || "Generation failed");
      const d = await r.json();
      const newLooks = d.looks || [];
      if (newLooks.length === 0) {
        toast.info("AI couldn't generate any new unique looks");
      } else {
        setLooks(prev => [...prev, ...newLooks]);
        toast.success(`${newLooks.length} new AI looks generated (unpublished)`);
      }
    } catch (e) { toast.error(e.message || "AI generation failed"); }
    setAiGenerating(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowNewMenu(false); };
    if (showNewMenu) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showNewMenu]);

  if (loading) return (
    <div className="min-h-screen bg-[#FAF8F0] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      {/* Header */}
      <div className="bg-[#282828] text-white px-6 py-4 flex items-center justify-between" data-testid="looks-manager-header">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/dashboard")} className="text-white/60 hover:text-white text-sm">&larr; Dashboard</button>
          <div className="w-px h-5 bg-white/20" />
          <Sparkles className="w-4 h-4 text-[#9A7B4F]" />
          <h1 className="text-lg font-medium">Looks Manager</h1>
          <span className="text-white/40 text-sm ml-2">{looks.length} looks</span>
        </div>
        <div className="flex items-center gap-3">
          <a href="/looks" target="_blank" rel="noreferrer" className="text-white/60 hover:text-white text-xs flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View Live</a>
          <div className="relative" ref={menuRef}>
            <Button onClick={() => setShowNewMenu(!showNewMenu)} className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-xs px-4" data-testid="create-look-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Look <ChevronDown className="w-3 h-3 ml-1.5" />
            </Button>
            {showNewMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-[#282828]/15 shadow-xl z-50 w-52 py-1" data-testid="new-look-menu">
                <button
                  onClick={() => { setShowNewMenu(false); setShowCreate(true); }}
                  className="w-full text-left px-4 py-2.5 hover:bg-[#FAF8F0] flex items-center gap-2.5 transition-colors"
                  data-testid="create-manual-btn"
                >
                  <Plus className="w-3.5 h-3.5 text-[#9A7B4F]" />
                  <div>
                    <p className="text-[12px] text-[#282828] font-medium">Create by myself</p>
                    <p className="text-[9px] text-[#676664]">Select items, AI names it</p>
                  </div>
                </button>
                <div className="border-t border-[#282828]/5 my-0.5" />
                <button
                  onClick={handleAiGenerate}
                  disabled={aiGenerating}
                  className="w-full text-left px-4 py-2.5 hover:bg-[#FAF8F0] flex items-center gap-2.5 transition-colors disabled:opacity-50"
                  data-testid="create-ai-btn"
                >
                  <Sparkles className="w-3.5 h-3.5 text-[#9A7B4F]" />
                  <div>
                    <p className="text-[12px] text-[#282828] font-medium">AI Generated</p>
                    <p className="text-[9px] text-[#676664]">Auto-create 4-8 new looks</p>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Generation Overlay */}
      {aiGenerating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" data-testid="ai-generating-overlay">
          <div className="bg-white p-8 text-center max-w-sm mx-4 shadow-2xl">
            <div className="relative mx-auto w-12 h-12 mb-4">
              <Sparkles className="w-12 h-12 text-[#9A7B4F] animate-pulse" />
            </div>
            <h3 className="text-lg text-[#282828] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>Generating Looks</h3>
            <p className="text-[12px] text-[#676664]">AI is creating new outfit combinations. This may take 15-30 seconds...</p>
            <Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F] mx-auto mt-4" />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <div className="bg-white border border-[#282828]/10 overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[40px_80px_1fr_140px_140px_120px_80px_80px_100px] gap-0 bg-[#F5F0E6] border-b border-[#282828]/10 text-[10px] tracking-wider uppercase text-[#676664] font-medium">
            <div className="p-3" />
            <div className="p-3">Cover</div>
            <div className="p-3">Name</div>
            <div className="p-3">Type</div>
            <div className="p-3">Occasion</div>
            <div className="p-3">Items</div>
            <div className="p-3 text-center">Status</div>
            <div className="p-3 text-center">AI</div>
            <div className="p-3 text-center">Actions</div>
          </div>

          {/* Rows */}
          {looks.map((look, idx) => (
            <LookRow
              key={look.id}
              look={look}
              idx={idx}
              saving={saving[look.id]}
              onUpdate={(updates) => updateLook(look.id, updates)}
              onDelete={() => deleteLook(look.id, look.name)}
              onUploadCover={(file) => uploadCover(look.id, file)}
              onEditItems={() => setEditItemsLook(look)}
              onOpenLayout={() => setLayoutLook(look)}
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              isDragging={dragIdx === idx}
            />
          ))}

          {looks.length === 0 && (
            <div className="p-12 text-center text-[#676664] text-sm">
              No looks yet. Click "New Look" or use AI Merchandising to generate looks.
            </div>
          )}
        </div>
      </div>

      {/* Create Look Modal */}
      {showCreate && (
        <CreateLookModal
          products={products}
          headers={headers}
          onCreated={(newLook) => { setLooks(prev => [...prev, newLook]); setShowCreate(false); }}
          onClose={() => setShowCreate(false)}
        />
      )}

      {editItemsLook && (
        <EditItemsModal
          look={editItemsLook}
          products={products}
          headers={headers}
          onSaved={(updatedLook) => {
            setLooks(prev => prev.map(l => l.id === updatedLook.id ? updatedLook : l));
            setEditItemsLook(null);
          }}
          onClose={() => setEditItemsLook(null)}
        />
      )}

      {layoutLook && (
        <LayoutBuilder
          look={layoutLook}
          products={products}
          headers={headers}
          onSaved={(updatedLook) => {
            setLooks(prev => prev.map(l => l.id === updatedLook.id ? { ...l, ...updatedLook } : l));
            setLayoutLook(null);
          }}
          onClose={() => setLayoutLook(null)}
        />
      )}
    </div>
  );
}

function LookRow({ look, idx, saving, onUpdate, onDelete, onUploadCover, onEditItems, onOpenLayout, onDragStart, onDragOver, onDragEnd, isDragging }) {
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showFocus, setShowFocus] = useState(false);
  const fileRef = useRef(null);

  const startEdit = (field) => {
    setEditing(field);
    setEditValue(look[field] || "");
  };

  const saveEdit = () => {
    if (editing && editValue !== (look[editing] || "")) {
      onUpdate({ [editing]: editValue });
    }
    setEditing(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") saveEdit();
    if (e.key === "Escape") setEditing(null);
  };

  const handleFocusClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    onUpdate({ cover_focus_x: x, cover_focus_y: y });
    setShowFocus(false);
  };

  return (
    <div
      className={`grid grid-cols-[40px_80px_1fr_140px_140px_120px_80px_80px_100px] gap-0 border-b border-[#282828]/5 items-center hover:bg-[#FAF8F0]/50 transition-colors ${isDragging ? 'opacity-50 bg-[#9A7B4F]/5' : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      data-testid={`look-row-${look.id}`}
    >
      {/* Drag handle */}
      <div className="p-3 cursor-grab active:cursor-grabbing flex justify-center">
        <GripVertical className="w-4 h-4 text-[#676664]/30" />
      </div>

      {/* Cover image */}
      <div className="p-2 relative">
        <div className="w-14 h-14 bg-[#f5f3f0] overflow-hidden relative">
          {look.cover_image ? (
            <img src={look.cover_image} alt="" className="w-full h-full object-cover" style={{ objectPosition: `${look.cover_focus_x || 50}% ${look.cover_focus_y || 50}%` }} />
          ) : look.items?.[0]?.image ? (
            <img src={look.items[0].image} alt="" className="w-full h-full object-cover opacity-60" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#676664]/30 text-[8px]">No img</div>
          )}
        </div>
        <div className="flex gap-1.5 mt-1">
          <button onClick={() => fileRef.current?.click()} className="text-[8px] text-[#9A7B4F] hover:underline flex items-center gap-0.5" title="Upload cover image" data-testid={`look-upload-${look.id}`}>
            <Camera className="w-2.5 h-2.5" />{saving ? "..." : "Upload"}
          </button>
          <button onClick={() => {
            const hasAnyImage = look.cover_image || (look.items && look.items.length > 0 && look.items[0].image);
            if (hasAnyImage) setShowFocus(true); else fileRef.current?.click();
          }} className="text-[8px] text-[#676664] hover:underline flex items-center gap-0.5" title="Set focal point" data-testid={`look-focus-${look.id}`}>
            <Crosshair className="w-2.5 h-2.5" />Focus
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) onUploadCover(e.target.files[0]); }} />
        {/* Focus picker modal */}
        {showFocus && (look.cover_image || (look.items && look.items.length > 0 && look.items[0].image)) && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => setShowFocus(false)}>
            <div className="bg-white p-4 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[12px] text-[#282828] font-medium">Click to set focal point</p>
                <button onClick={() => setShowFocus(false)} className="text-[#676664] hover:text-[#282828]"><X className="w-4 h-4" /></button>
              </div>
              <div className="relative cursor-crosshair overflow-hidden aspect-[4/3]" onClick={handleFocusClick}>
                <img src={look.cover_image || look.items?.[0]?.image} alt="" className="w-full h-full object-cover" />
                <div
                  className="absolute w-6 h-6 border-2 border-white rounded-full shadow-lg transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ left: `${look.cover_focus_x || 50}%`, top: `${look.cover_focus_y || 50}%`, boxShadow: '0 0 0 2px rgba(154,123,79,0.8), 0 2px 8px rgba(0,0,0,0.3)' }}
                />
              </div>
              <p className="text-[10px] text-[#676664] mt-2">Focus: {look.cover_focus_x || 50}% x, {look.cover_focus_y || 50}% y</p>
            </div>
          </div>
        )}
      </div>

      {/* Name — inline editable */}
      <div className="p-3">
        {editing === "name" ? (
          <input
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={handleKeyDown}
            className="w-full text-[13px] text-[#282828] bg-white border border-[#9A7B4F]/30 px-2 py-1 outline-none"
            data-testid={`edit-name-${look.id}`}
          />
        ) : (
          <div className="cursor-pointer group" onClick={() => startEdit("name")}>
            <p className="text-[13px] text-[#282828] font-medium group-hover:text-[#9A7B4F] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {look.name || "Untitled"}
            </p>
            {look.description && <p className="text-[10px] text-[#676664] truncate mt-0.5 max-w-[250px]">{look.description}</p>}
          </div>
        )}
      </div>

      {/* Type — inline editable */}
      <div className="p-3">
        {editing === "look_type" ? (
          <input
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={handleKeyDown}
            className="w-full text-[11px] bg-white border border-[#9A7B4F]/30 px-2 py-1 outline-none"
            placeholder="e.g. Bridal"
            data-testid={`edit-type-${look.id}`}
          />
        ) : (
          <span
            onClick={() => startEdit("look_type")}
            className={`text-[11px] cursor-pointer px-2 py-0.5 inline-block ${look.look_type ? 'bg-[#9A7B4F]/10 text-[#9A7B4F]' : 'text-[#676664]/40 hover:text-[#9A7B4F]'}`}
          >
            {look.look_type || "+ type"}
          </span>
        )}
      </div>

      {/* Occasion — inline editable */}
      <div className="p-3">
        {editing === "occasion" ? (
          <input
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={handleKeyDown}
            className="w-full text-[11px] bg-white border border-[#9A7B4F]/30 px-2 py-1 outline-none"
            placeholder="e.g. Wedding"
            data-testid={`edit-occasion-${look.id}`}
          />
        ) : (
          <span
            onClick={() => startEdit("occasion")}
            className={`text-[11px] cursor-pointer px-2 py-0.5 inline-block ${look.occasion ? 'bg-[#282828]/5 text-[#676664]' : 'text-[#676664]/40 hover:text-[#9A7B4F]'}`}
          >
            {look.occasion || "+ occasion"}
          </span>
        )}
      </div>

      {/* Items count + thumbnails + edit button */}
      <div className="p-3">
        <div className="flex items-center gap-1">
          {look.items?.slice(0, 3).map(item => (
            <div key={item.id} className="w-6 h-6 bg-[#f5f3f0] overflow-hidden rounded-sm">
              {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
            </div>
          ))}
          {(look.items?.length || 0) > 3 && <span className="text-[9px] text-[#676664]">+{look.items.length - 3}</span>}
          <span className="text-[10px] text-[#676664] ml-1">{look.items?.length || 0}pc</span>
          <button onClick={onEditItems} className="ml-auto p-1 text-[#9A7B4F] hover:bg-[#9A7B4F]/10 rounded transition-colors" title="Edit items">
            <Pencil className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Published toggle */}
      <div className="p-3 flex justify-center">
        <button
          onClick={() => onUpdate({ published: !look.published })}
          className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors ${look.published ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}
          data-testid={`toggle-publish-${look.id}`}
        >
          {look.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          {look.published ? "Live" : "Draft"}
        </button>
      </div>

      {/* AI badge */}
      <div className="p-3 flex justify-center">
        {look.ai_generated ? (
          <span className="text-[9px] bg-[#9A7B4F]/10 text-[#9A7B4F] px-2 py-0.5 rounded">AI</span>
        ) : (
          <span className="text-[9px] bg-[#282828]/5 text-[#676664] px-2 py-0.5 rounded">Manual</span>
        )}
      </div>

      {/* Actions */}
      <div className="p-3 flex items-center justify-center gap-1">
        <button onClick={onOpenLayout} className="p-1.5 text-[#676664] hover:text-[#9A7B4F] hover:bg-[#9A7B4F]/5 rounded transition-colors" title="Layout Builder" data-testid={`layout-look-${look.id}`}>
          <LayoutGrid className="w-3.5 h-3.5" />
        </button>
        <a href={`/looks/${look.id}`} target="_blank" rel="noreferrer" className="p-1.5 text-[#676664] hover:text-[#9A7B4F] hover:bg-[#9A7B4F]/5 rounded transition-colors" title="Preview">
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <button onClick={onDelete} className="p-1.5 text-[#676664] hover:text-red-500 hover:bg-red-50 rounded transition-colors" data-testid={`delete-look-${look.id}`} title="Delete">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function CreateLookModal({ products, headers, onCreated, onClose }) {
  const [name, setName] = useState("");
  const [lookType, setLookType] = useState("");
  const [occasion, setOccasion] = useState("");
  const [description, setDescription] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [step, setStep] = useState(1); // 1 = select items, 2 = review & create
  const [autoDescribing, setAutoDescribing] = useState(false);

  const filtered = products.filter(p =>
    (p.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (p.category || "").toLowerCase().includes(search.toLowerCase())
  );

  const toggleProduct = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAutoDescribe = async () => {
    if (selectedIds.length < 2) { toast.error("Select at least 2 products"); return; }
    setAutoDescribing(true);
    try {
      const r = await fetch(`${API}/ai/admin/looks/auto-describe`, {
        method: "POST", headers,
        body: JSON.stringify({ product_ids: selectedIds }),
      });
      if (!r.ok) throw new Error("Auto-describe failed");
      const d = await r.json();
      setName(d.name || "");
      setLookType(d.look_type || "");
      setOccasion(d.occasion || "");
      setDescription(d.description || "");
      setStep(2);
    } catch {
      // Fallback — go to step 2 with empty fields for manual entry
      toast.error("AI couldn't generate details. Fill in manually.");
      setStep(2);
    }
    setAutoDescribing(false);
  };

  const handleCreate = async () => {
    if (selectedIds.length < 2) { toast.error("Select at least 2 products"); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/ai/admin/looks`, {
        method: "POST", headers,
        body: JSON.stringify({ name: name || "New Look", look_type: lookType, occasion, description, product_ids: selectedIds, hero_product_id: selectedIds[0] }),
      });
      const newLook = await r.json();
      newLook.items = selectedIds.map(id => {
        const p = products.find(x => x.id === id);
        return { id, name: p?.name || "", image: p?.images?.[0] || "", price_4_day: p?.price_4_day || 0 };
      });
      onCreated(newLook);
      toast.success("Look created");
    } catch { toast.error("Failed to create"); }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-look-modal">
      <div className="bg-white max-w-[700px] w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Modal header */}
        <div className="flex items-center justify-between p-5 border-b border-[#282828]/10">
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button onClick={() => setStep(1)} className="p-1 hover:bg-[#282828]/5 rounded text-[#676664] hover:text-[#282828]" data-testid="create-look-back">
                <span className="text-sm">&larr;</span>
              </button>
            )}
            <h2 className="text-lg text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
              {step === 1 ? "Select Products" : "Review & Create"}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#282828]/5 rounded"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {step === 1 ? (
            /* Step 1: Product picker */
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] text-[#676664] tracking-wider uppercase">Select Products ({selectedIds.length} selected)</label>
                {selectedIds.length > 0 && <button onClick={() => setSelectedIds([])} className="text-[10px] text-[#9A7B4F] hover:underline">Clear</button>}
              </div>
              <input value={search} onChange={e => setSearch(e.target.value)} className="w-full border border-[#282828]/15 px-3 py-2 text-sm outline-none focus:border-[#9A7B4F] mb-2" placeholder="Search products..." data-testid="create-look-search" />
              <div className="grid grid-cols-5 gap-2 max-h-[350px] overflow-y-auto p-1">
                {filtered.slice(0, 50).map(p => {
                  const selected = selectedIds.includes(p.id);
                  return (
                    <button key={p.id} onClick={() => toggleProduct(p.id)} className={`text-left rounded overflow-hidden transition-all ${selected ? 'ring-2 ring-[#9A7B4F] ring-offset-1' : 'opacity-70 hover:opacity-100'}`} data-testid={`product-pick-${p.id}`}>
                      <div className="aspect-[3/4] bg-[#f5f3f0] overflow-hidden relative">
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" loading="lazy" />}
                        {selected && <div className="absolute top-0.5 right-0.5 w-4 h-4 bg-[#9A7B4F] rounded-full flex items-center justify-center"><span className="text-white text-[8px] font-bold">{selectedIds.indexOf(p.id) + 1}</span></div>}
                      </div>
                      <p className="text-[9px] text-[#282828] truncate px-1 py-0.5">{p.name}</p>
                      <p className="text-[8px] text-[#676664] truncate px-1 pb-0.5">{p.category}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Step 2: Review AI-generated details (editable) */
            <div className="space-y-4">
              {/* Selected items preview */}
              <div>
                <label className="text-[10px] text-[#676664] tracking-wider uppercase block mb-2">Selected Products ({selectedIds.length})</label>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {selectedIds.map(id => {
                    const p = products.find(x => x.id === id);
                    return (
                      <div key={id} className="flex-shrink-0 w-14">
                        <div className="w-14 h-16 bg-[#f5f3f0] overflow-hidden rounded">
                          {p?.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <p className="text-[8px] text-[#282828] truncate mt-0.5">{p?.name || "?"}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* AI-generated fields (editable) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-[#676664] tracking-wider uppercase block mb-1">Name <span className="text-[#9A7B4F]">(AI generated)</span></label>
                  <input value={name} onChange={e => setName(e.target.value)} className="w-full border border-[#282828]/15 px-3 py-2 text-sm outline-none focus:border-[#9A7B4F]" placeholder="e.g. Royal Wedding Look" data-testid="create-look-name" />
                </div>
                <div>
                  <label className="text-[10px] text-[#676664] tracking-wider uppercase block mb-1">Look Type</label>
                  <input value={lookType} onChange={e => setLookType(e.target.value)} className="w-full border border-[#282828]/15 px-3 py-2 text-sm outline-none focus:border-[#9A7B4F]" placeholder="e.g. Bridal, Casual" data-testid="create-look-type" />
                </div>
                <div>
                  <label className="text-[10px] text-[#676664] tracking-wider uppercase block mb-1">Occasion</label>
                  <input value={occasion} onChange={e => setOccasion(e.target.value)} className="w-full border border-[#282828]/15 px-3 py-2 text-sm outline-none focus:border-[#9A7B4F]" placeholder="e.g. Wedding" data-testid="create-look-occasion" />
                </div>
                <div>
                  <label className="text-[10px] text-[#676664] tracking-wider uppercase block mb-1">Description</label>
                  <input value={description} onChange={e => setDescription(e.target.value)} className="w-full border border-[#282828]/15 px-3 py-2 text-sm outline-none focus:border-[#9A7B4F]" placeholder="Short description" data-testid="create-look-desc" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-[#282828]/10 flex items-center justify-between">
          <p className="text-[11px] text-[#676664]">{selectedIds.length < 2 ? "Select at least 2 products" : `${selectedIds.length} products selected`}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="text-xs">Cancel</Button>
            {step === 1 ? (
              <Button onClick={handleAutoDescribe} disabled={selectedIds.length < 2 || autoDescribing} className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-xs px-6" data-testid="create-look-next">
                {autoDescribing ? <><Loader2 className="w-3 h-3 animate-spin mr-1.5" /> Generating...</> : <><Sparkles className="w-3 h-3 mr-1.5" /> Next — AI Name &amp; Details</>}
              </Button>
            ) : (
              <Button onClick={handleCreate} disabled={selectedIds.length < 2 || creating} className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-xs px-6" data-testid="create-look-submit">
                {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Plus className="w-3 h-3 mr-1.5" />}
                Create Look
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function EditItemsModal({ look, products, headers, onSaved, onClose }) {
  const [selectedIds, setSelectedIds] = useState(new Set(look.product_ids || look.items?.map(i => i.id) || []));
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const toggle = (id) => setSelectedIds(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/ai/admin/looks/${look.id}`, {
        method: "PUT", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ product_ids: [...selectedIds] })
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      onSaved(d.look || { ...look, product_ids: [...selectedIds] });
    } catch { alert("Failed to save"); }
    setSaving(false);
  };

  const filtered = products.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-[#282828]/10 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[#282828]">Edit Items — {look.name}</h3>
            <p className="text-[11px] text-[#676664] mt-0.5">{selectedIds.size} products selected</p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 border-b border-[#282828]/10">
          <input
            type="text" placeholder="Search products..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-[#282828]/15 bg-white focus:outline-none focus:border-[#9A7B4F]"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-4 gap-3">
            {filtered.map(p => {
              const sel = selectedIds.has(p.id);
              const img = Array.isArray(p.images) ? p.images[0] : p.image;
              return (
                <div key={p.id} onClick={() => toggle(p.id)}
                  className={`cursor-pointer border-2 transition-all ${sel ? "border-[#9A7B4F] bg-[#9A7B4F]/5" : "border-transparent hover:border-[#282828]/20"}`}>
                  <div className="relative aspect-[3/4] bg-[#f5f3f0]">
                    {img && <img src={img} alt="" className="w-full h-full object-cover" />}
                    {sel && <div className="absolute top-1 right-1 w-5 h-5 bg-[#9A7B4F] rounded-full flex items-center justify-center"><span className="text-white text-[10px] font-bold">✓</span></div>}
                  </div>
                  <p className="text-[10px] text-[#282828] p-1.5 truncate">{p.name}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-5 border-t border-[#282828]/10 flex items-center justify-between">
          <p className="text-[11px] text-[#676664]">{selectedIds.size < 2 ? "Select at least 2 products" : `${selectedIds.size} products selected`}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="text-xs">Cancel</Button>
            <Button onClick={handleSave} disabled={selectedIds.size < 2 || saving} className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-xs px-6">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Save className="w-3 h-3 mr-1.5" />}
              Save Items
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
